/**
 * Pure helpers for the RynCore token-cost reads. Angular-free so both
 * platforms and the tests consume them directly.
 */
import {
  CostOverviewCard,
  DailyCostBar,
  TokenByModelRow,
  TokenDayEntry,
  TokenHostRow,
  TokenProjectRow,
  TokenSummary,
} from './token-cost.models';

/**
 * Dollar label without Intl (the NS runtime's Intl support varies by
 * platform): whole dollars with thousands separators from $1,000 up,
 * cents below that.
 */
export function formatUsd(amount: number): string {
  const negative = amount < 0;
  const abs = Math.abs(amount);
  const text =
    abs >= 1000
      ? Math.round(abs)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      : abs.toFixed(2);
  return `${negative ? '-' : ''}$${text}`;
}

/** Compact token count: 1.2B / 34M / 56K, plain digits below 1K. */
export function formatCompactTokens(tokens: number): string {
  if (tokens >= 1_000_000_000) return `${(tokens / 1_000_000_000).toFixed(1)}B`;
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(0)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(0)}K`;
  return `${tokens}`;
}

/**
 * The three overview cards. Cards whose period is entirely missing from the
 * payload are omitted rather than rendered as zero, so "no data yet" cannot
 * read as "$0 spent".
 */
export function buildCostOverview(summary: TokenSummary): CostOverviewCard[] {
  const cards: CostOverviewCard[] = [];
  if (summary.today) {
    cards.push({
      key: 'today',
      label: 'Today',
      cost: summary.today.totalCost,
      costWithoutCache: summary.today_cost_without_cache,
      tokens: summary.today.totalTokens,
      days: null,
    });
  }
  if (summary.last_7_days) {
    cards.push({
      key: 'week',
      label: 'Last 7 days',
      cost: summary.last_7_days.total_cost,
      costWithoutCache: summary.last_7_days.cost_without_cache,
      tokens: summary.last_7_days.total_tokens,
      days: summary.last_7_days.days,
    });
  }
  if (summary.total) {
    cards.push({
      key: 'total',
      label: 'All time',
      cost: summary.total.total_cost,
      costWithoutCache: summary.total.cost_without_cache,
      tokens: summary.total.total_tokens,
      days: summary.total.days,
    });
  }
  return cards;
}

/**
 * Last `count` daily entries as bars scaled to the window's max cost.
 * Assumes the API's date-ascending order and preserves it (oldest first).
 */
export function recentDailyCostBars(
  daily: TokenDayEntry[],
  count: number
): DailyCostBar[] {
  const recent = daily.slice(-Math.max(0, count));
  const max = recent.reduce((m, d) => Math.max(m, d.totalCost), 0);
  return recent.map((d) => ({
    date: d.date,
    cost: d.totalCost,
    percentOfMax: max > 0 ? Math.round((d.totalCost / max) * 100) : 0,
  }));
}

export function topModelsByCost(
  rows: TokenByModelRow[],
  count: number
): TokenByModelRow[] {
  return [...rows].sort((a, b) => b.cost - a.cost).slice(0, count);
}

export function sortHostsByCost(rows: TokenHostRow[]): TokenHostRow[] {
  return [...rows].sort((a, b) => b.cost_usd - a.cost_usd);
}

export function topProjectsByCost(
  rows: TokenProjectRow[],
  count: number
): TokenProjectRow[] {
  return [...rows].sort((a, b) => b.cost_usd - a.cost_usd).slice(0, count);
}
